/**
 * Secure TikTok Connect Button Component (PX-267)
 * Implements production-ready OAuth flow with comprehensive security measures
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Button, CircularProgress, Alert, Box } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import TikTokLogo from './TikTokLogo';

import { useStatus } from '../providers/MsgStatusProvider';
import { useAuth } from '../providers/AuthProvider';
import { useTokenValidation } from '../providers/TokenValidationProvider';
import { logInfo as loggerInfo, logError as loggerError, logWarning as loggerWarning } from '../utils/logger';

const TikTokConnectButtonSecure = ({ 
  onConnectionSuccess,
  onConnectionError,
  disabled = false,
  variant = "contained",
  size = "medium"
}) => {
  // State management
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [hasProcessedCallback, setHasProcessedCallback] = useState(false);
  
  // Hooks
  const location = useLocation();
  const navigate = useNavigate();
  const { setStatusType, setStatusMessage, setIsAlertOpen } = useStatus();
  const { user, token } = useAuth();
  const { forceValidation, clearCache } = useTokenValidation();
  
  // Refs for cleanup
  const abortControllerRef = useRef(null);
  const timeoutRef = useRef(null);
  
  // Configuration
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const REQUEST_TIMEOUT = 30000; // 30 seconds
  
  /**
   * Validate environment configuration
   */
  const validateEnvironment = useCallback(() => {
    if (!API_BASE_URL) {
      const error = 'API base URL not configured';
      loggerError('TikTok OAuth: Missing API base URL', { 
        component: 'TikTokConnectButtonSecure',
        user_id: user?.id 
      });
      return error;
    }
    
    if (!token) {
      const error = 'User authentication required';
      loggerWarning('TikTok OAuth: No auth token available', { 
        component: 'TikTokConnectButtonSecure',
        user_id: user?.id 
      });
      return error;
    }
    
    return null;
  }, [API_BASE_URL, token, user?.id]);

  /**
   * Secure API request helper
   */
  const makeSecureRequest = useCallback(async (endpoint, options = {}) => {
    const envError = validateEnvironment();
    if (envError) {
      throw new Error(envError);
    }

    // Create abort controller for this request
    abortControllerRef.current = new AbortController();
    
    const requestOptions = {
      signal: abortControllerRef.current.signal,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Token ${token}`,
        ...options.headers,
      },
      ...options,
    };

    // Set timeout
    timeoutRef.current = setTimeout(() => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    }, REQUEST_TIMEOUT);

    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, requestOptions);
      
      clearTimeout(timeoutRef.current);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      clearTimeout(timeoutRef.current);
      
      if (error.name === 'AbortError') {
        throw new Error('Request timed out');
      }
      
      throw error;
    }
  }, [API_BASE_URL, token, validateEnvironment]);

  /**
   * Validate redirect URL for security
   */
  const validateRedirectUrl = useCallback((url) => {
    if (!url || typeof url !== 'string') {
      return false;
    }

    try {
      const parsed = new URL(url);
      const allowedDomains = ['tiktok.com', 'www.tiktok.com'];
      
      return (
        parsed.protocol === 'https:' &&
        allowedDomains.includes(parsed.hostname) &&
        parsed.pathname.includes('/auth/authorize')
      );
    } catch {
      return false;
    }
  }, []);

  /**
   * Sanitize error messages for display
   */
  const sanitizeErrorMessage = useCallback((error) => {
    if (!error) return 'An unexpected error occurred';
    
    const message = typeof error === 'string' ? error : error.message || 'Unknown error';
    
    // Remove potentially sensitive information
    return message
      .replace(/Token\s+\w+/gi, 'Token [REDACTED]')
      .replace(/Authorization:\s*\w+/gi, 'Authorization: [REDACTED]')
      .replace(/bearer\s+\w+/gi, 'Bearer [REDACTED]')
      .substring(0, 200); // Limit length
  }, []);

  /**
   * Handle TikTok OAuth initiation
   */
  const handleConnect = useCallback(async () => {
    if (isConnecting || disabled) {
      return;
    }

    setIsConnecting(true);
    setConnectionError(null);

    loggerInfo('TikTok OAuth: Connection initiated', {
      component: 'TikTokConnectButtonSecure',
      user_id: user?.id,
      timestamp: new Date().toISOString()
    });

    try {
      const data = await makeSecureRequest('connect/tiktok/auth/', {
        method: 'POST',
      });

      if (!data.redirect_url) {
        throw new Error('No redirect URL received from server');
      }

      if (!validateRedirectUrl(data.redirect_url)) {
        throw new Error('Invalid redirect URL received');
      }

      loggerInfo('TikTok OAuth: Redirect URL validated', {
        component: 'TikTokConnectButtonSecure',
        user_id: user?.id,
        redirect_domain: new URL(data.redirect_url).hostname,
        has_csrf_state: !!data.csrf_state
      });

      // Redirect to TikTok OAuth
      window.location.href = data.redirect_url;
      
    } catch (error) {
      const sanitizedError = sanitizeErrorMessage(error);
      
      loggerError('TikTok OAuth: Connection failed', {
        component: 'TikTokConnectButtonSecure',
        user_id: user?.id,
        error: sanitizedError,
        timestamp: new Date().toISOString()
      });

      setConnectionError(sanitizedError);
      setIsConnecting(false);

      // Show user-friendly error message
      setStatusType('error');
      setStatusMessage('Failed to connect to TikTok. Please try again.');
      setIsAlertOpen(true);

      if (onConnectionError) {
        onConnectionError(error);
      }
    }
  }, [
    isConnecting,
    disabled,
    user?.id,
    makeSecureRequest,
    validateRedirectUrl,
    sanitizeErrorMessage,
    setStatusType,
    setStatusMessage,
    setIsAlertOpen,
    onConnectionError
  ]);

  /**
   * Handle OAuth callback from TikTok
   */
  const handleOAuthCallback = useCallback(async () => {
    const searchParams = new URLSearchParams(location.search);
    const code = searchParams.get('code');
    const state = searchParams.get('state');

    if (!code || !state) {
      return; // Not a TikTok OAuth callback
    }

    if (hasProcessedCallback) {
      return; // Already processed
    }

    setHasProcessedCallback(true);
    setIsConnecting(true);

    loggerInfo('TikTok OAuth: Processing callback', {
      component: 'TikTokConnectButtonSecure',
      user_id: user?.id,
      has_code: !!code,
      has_state: !!state
    });

    try {
      const data = await makeSecureRequest('connect/tiktok/callback/', {
        method: 'POST',
        body: JSON.stringify({
          code: code,
          state: state
        }),
      });

      if (data.message && data.message.includes('successfully')) {
        const operation = data.operation || 'connected';
        
        loggerInfo('TikTok OAuth: Operation completed successfully', {
          component: 'TikTokConnectButtonSecure',
          user_id: user?.id,
          tiktok_username: data.account?.username,
          account_type: data.account?.account_type,
          operation: operation
        });

        // Show success message based on operation
        setStatusType('success');
        if (operation === 'refreshed') {
          setStatusMessage('TikTok account tokens refreshed successfully!');
        } else if (operation === 'reconnected') {
          setStatusMessage('TikTok account reconnected successfully!');
        } else {
          setStatusMessage('TikTok account connected successfully!');
        }
        setIsAlertOpen(true);

        // Clear URL parameters
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);

        // Clear validation cache and force fresh validation after successful refresh/connection
        try {
          // Clear any cached validation data first
          clearCache();
          
          // Then force fresh validation
          await forceValidation();
          
          loggerInfo('TikTok OAuth: Token validation cache cleared and forced after successful connection', {
            component: 'TikTokConnectButtonSecure',
            user_id: user?.id,
            operation: operation,
            account_id: data.account?.id
          });
        } catch (validationError) {
          loggerError('TikTok OAuth: Failed to force validation after connection', {
            component: 'TikTokConnectButtonSecure',
            user_id: user?.id,
            error: validationError.message
          });
        }

        // Navigate to socials page or call success callback
        if (onConnectionSuccess) {
          onConnectionSuccess(data.account);
        } else if (BASE_URL) {
          navigate('/connect/socials');
        }
      } else {
        throw new Error(data.error || 'Unexpected response from server');
      }
    } catch (error) {
      const sanitizedError = sanitizeErrorMessage(error);
      
      loggerError('TikTok OAuth: Callback failed', {
        component: 'TikTokConnectButtonSecure',
        user_id: user?.id,
        error: sanitizedError
      });

      setConnectionError(sanitizedError);

      // Show error message
      setStatusType('error');
      setStatusMessage('Failed to complete TikTok connection. Please try again.');
      setIsAlertOpen(true);

      if (onConnectionError) {
        onConnectionError(error);
      }
    } finally {
      setIsConnecting(false);
    }
  }, [
    location.search,
    hasProcessedCallback,
    user?.id,
    makeSecureRequest,
    sanitizeErrorMessage,
    setStatusType,
    setStatusMessage,
    setIsAlertOpen,
    onConnectionSuccess,
    onConnectionError,
    BASE_URL,
    navigate,
    clearCache,
    forceValidation
  ]);

  /**
   * Effect to handle OAuth callback
   */
  useEffect(() => {
    handleOAuthCallback();
  }, [handleOAuthCallback]);

  /**
   * Cleanup effect
   */
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  /**
   * Throttle button clicks to prevent rapid requests
   */
  const throttledHandleConnect = useCallback(() => {
    if (isConnecting) {
      return;
    }
    handleConnect();
  }, [handleConnect, isConnecting]);

  return (
    <Box>
      {connectionError && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          onClose={() => setConnectionError(null)}
        >
          {connectionError}
        </Alert>
      )}
      
      <Button
        variant={variant}
        size={size}
        startIcon={isConnecting ? <CircularProgress size={20} /> : <TikTokLogo />}
        onClick={throttledHandleConnect}
        disabled={disabled || isConnecting || !token}
        sx={{
          minWidth: 200,
          position: 'relative',
        }}
        aria-label="Connect TikTok account"
      >
        {isConnecting ? 'Connecting...' : 'Connect to TikTok'}
      </Button>
    </Box>
  );
};

export default TikTokConnectButtonSecure;